import React from 'react';
import { cn } from '@/lib/utils';

/**
 * ListCard is a generic component for rendering lists of items with a title
 * and optional action (e.g., link to full list). It handles empty state
 * gracefully and uses `divide-y` to separate items.
 */
export interface ListCardProps<Item> {
  title: string;
  items: Item[];
  renderItem: (item: Item, index: number) => React.ReactNode;
  getItemKey?: (item: Item, index: number) => React.Key;
  emptyState?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /** `card` = elevated panel; `flat` = seamless list with divider lines only */
  variant?: 'card' | 'flat';
  /** `divided` = flat rows separated by dividers; `inset` = bordered rows with spacing (open-design attention list) */
  itemsLayout?: 'divided' | 'inset';
}

export function ListCard<Item>({
  title,
  items,
  renderItem,
  getItemKey,
  emptyState,
  action,
  className,
  variant = 'card',
  itemsLayout = 'divided',
}: ListCardProps<Item>) {
  const isFlat = variant === 'flat';
  const isInset = itemsLayout === 'inset';

  return (
    <section
      className={cn(
        'flex w-full min-w-0 h-fit flex-col overflow-hidden rounded-lg border border-line bg-surface text-fg shadow-sm',
        /** Flat dashboards still need a semantic surface token so dark/light shells don't "float" unpainted canvases over the body (#fff fallbacks per ui-ux.md). */
        isFlat && 'py-1 px-3 sm:px-4',
        className,
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between bg-surface',
          isFlat ? 'mb-3' : 'border-b border-line px-4 py-4 sm:px-6',
        )}
      >
        <h2 className={cn('font-semibold text-fg', isFlat ? 'text-base' : 'text-lg')}>
          {title}
        </h2>
        {action && <div>{action}</div>}
      </div>
      {items.length > 0 ? (
        <ul
          className={cn(
            'm-0 w-full min-w-0 flex-shrink-0 list-none',
            !isFlat && 'px-4 pb-4 sm:px-6 sm:pb-6',
            isInset ? 'flex flex-col gap-2 pt-3 sm:gap-3 sm:pt-4' : 'pt-0',
            !isInset && cn('divide-y divide-line', isFlat && 'border-y border-line'),
          )}
        >
          {items.map((item, idx) => (
            <li
              key={getItemKey ? getItemKey(item, idx) : idx}
              className={cn(
                'box-border w-full min-w-0',
                isInset
                  ? 'list-card-inset overflow-hidden rounded-lg border border-line p-3 sm:p-4'
                  : cn('py-3', !isFlat && 'first:pt-0 last:pb-0'),
              )}
            >
              {renderItem(item, idx)}
            </li>
          ))}
        </ul>
      ) : (
        <div
          className={cn(
            'flex items-center justify-center text-sm text-muted',
            !isFlat && 'px-4 pb-8 pt-4 sm:px-6 sm:pb-6',
            isFlat ? 'py-4' : 'sm:py-6',
          )}
        >
          {emptyState || 'No items'}
        </div>
      )}
    </section>
  );
}

export default ListCard;
